import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Default translations for common UI elements
const DEFAULT_TRANSLATIONS = {
  en: {
    // Navigation
    "nav.home": "Home",
    "nav.pricing": "Pricing",
    "nav.marketplace": "Seed Shop",
    "nav.weather": "Weather",
    "nav.plants": "Plant Database",
    "nav.calendar": "Planting Calendar",
    "nav.contact": "Contact",
    "nav.login": "Log In",
    "nav.logout": "Log Out",
    "nav.profile": "Profile",
    
    // Common actions
    "action.save": "Save",
    "action.cancel": "Cancel",
    "action.delete": "Delete",
    "action.edit": "Edit",
    "action.create": "Create",
    "action.search": "Search",
    "action.filter": "Filter",
    "action.back": "Back",
    "action.next": "Next",
    "action.submit": "Submit",
    "action.upload": "Upload",
    "action.download": "Download",
    
    // Landing page
    "landing.hero.title": "Design Your Dream Garden",
    "landing.hero.subtitle": "Smart planning for sustainable gardens based on permaculture principles",
    "landing.cta.start": "Start Planning",
    "landing.cta.learn": "Learn More",
    "landing.features.title": "Features",
    "landing.curb_appeal": "Add curb appeal to your property",
    
    // Garden planning
    "garden.title": "Garden Planner",
    "garden.new": "New Garden Plan",
    "garden.saved": "Saved Plans",
    "garden.location": "Location",
    "garden.dimensions": "Dimensions",
    "garden.aesthetic": "Garden Style",
    
    // Weather
    "weather.title": "Weather Forecast",
    "weather.current": "Current Conditions",
    "weather.forecast": "7-Day Forecast",
    "weather.historical": "Historical Data",
    "weather.advisory": "Garden Advisory",
    
    // Plants
    "plants.title": "Plant Database",
    "plants.search": "Search plants...",
    "plants.spacing": "Spacing",
    "plants.sun": "Sun Requirements",
    "plants.water": "Water Needs",
    "plants.harvest": "Days to Harvest",
    
    // Visual Design Tool
    "design.title": "Visual Design Tool",
    "design.upload": "Upload Photo",
    "design.plants": "Plants",
    "design.warnings": "Warnings",
    "design.spacing_warning": "Spacing Issue",
    "design.rotation_warning": "Crop Rotation Warning",
    "design.no_warnings": "No warnings! Your garden layout looks good.",
    "design.previous_season": "Previous Season",
    "design.current_season": "Current Season",
    
    // Pricing
    "pricing.title": "Choose Your Plan",
    "pricing.free": "Free",
    "pricing.standard": "The Gardener",
    "pricing.premium": "Master Gardener",
    "pricing.lifetime": "Lifetime Access",
    "pricing.monthly": "per month",
    "pricing.yearly": "per year",
    "pricing.one_time": "one-time payment",
    
    // Auth
    "auth.login": "Log In",
    "auth.logout": "Log Out",
    "auth.login_required": "Please log in to continue",
    
    // Messages
    "msg.success": "Success!",
    "msg.error": "Something went wrong",
    "msg.loading": "Loading...",
    "msg.saving": "Saving...",
  },
  es: {
    // Navigation
    "nav.home": "Inicio",
    "nav.pricing": "Precios",
    "nav.marketplace": "Tienda de Semillas",
    "nav.weather": "Clima",
    "nav.plants": "Base de Plantas",
    "nav.calendar": "Calendario de Siembra",
    "nav.contact": "Contacto",
    "nav.login": "Iniciar Sesión",
    "nav.logout": "Cerrar Sesión",
    "nav.profile": "Perfil",
    
    // Common actions
    "action.save": "Guardar",
    "action.cancel": "Cancelar",
    "action.delete": "Eliminar",
    "action.edit": "Editar",
    "action.create": "Crear",
    "action.search": "Buscar",
    "action.filter": "Filtrar",
    "action.back": "Volver",
    "action.next": "Siguiente",
    "action.submit": "Enviar",
    "action.upload": "Subir",
    "action.download": "Descargar",
    
    // Landing page
    "landing.hero.title": "Diseña el Jardín de tus Sueños",
    "landing.hero.subtitle": "Planificación inteligente para jardines sostenibles basados en principios de permacultura",
    "landing.cta.start": "Comenzar a Planificar",
    "landing.cta.learn": "Más Información",
    "landing.features.title": "Características",
    "landing.curb_appeal": "Añade atractivo visual a tu propiedad",
    
    // Garden planning
    "garden.title": "Planificador de Jardín",
    "garden.new": "Nuevo Plan de Jardín",
    "garden.saved": "Planes Guardados",
    "garden.location": "Ubicación",
    "garden.dimensions": "Dimensiones",
    "garden.aesthetic": "Estilo de Jardín",
    
    // Weather
    "weather.title": "Pronóstico del Tiempo",
    "weather.current": "Condiciones Actuales",
    "weather.forecast": "Pronóstico de 7 Días",
    "weather.historical": "Datos Históricos",
    "weather.advisory": "Consejos para el Jardín",
    
    // Plants
    "plants.title": "Base de Datos de Plantas",
    "plants.search": "Buscar plantas...",
    "plants.spacing": "Espaciado",
    "plants.sun": "Requisitos de Sol",
    "plants.water": "Necesidades de Agua",
    "plants.harvest": "Días hasta Cosecha",
    
    // Visual Design Tool
    "design.title": "Herramienta de Diseño Visual",
    "design.upload": "Subir Foto",
    "design.plants": "Plantas",
    "design.warnings": "Advertencias",
    "design.spacing_warning": "Problema de Espaciado",
    "design.rotation_warning": "Advertencia de Rotación de Cultivos",
    "design.no_warnings": "¡Sin advertencias! Tu diseño de jardín se ve bien.",
    "design.previous_season": "Temporada Anterior",
    "design.current_season": "Temporada Actual",
    
    // Pricing
    "pricing.title": "Elige tu Plan",
    "pricing.free": "Gratis",
    "pricing.standard": "El Jardinero",
    "pricing.premium": "Jardinero Experto",
    "pricing.lifetime": "Acceso de por Vida",
    "pricing.monthly": "por mes",
    "pricing.yearly": "por año",
    "pricing.one_time": "pago único",
    
    // Auth
    "auth.login": "Iniciar Sesión",
    "auth.logout": "Cerrar Sesión",
    "auth.login_required": "Por favor inicia sesión para continuar",
    
    // Messages
    "msg.success": "¡Éxito!",
    "msg.error": "Algo salió mal",
    "msg.loading": "Cargando...",
    "msg.saving": "Guardando...",
  },
  fr: {
    // Navigation
    "nav.home": "Accueil",
    "nav.pricing": "Tarifs",
    "nav.marketplace": "Boutique de Graines",
    "nav.weather": "Météo",
    "nav.plants": "Base de Plantes",
    "nav.calendar": "Calendrier de Plantation",
    "nav.contact": "Contact",
    "nav.login": "Connexion",
    "nav.logout": "Déconnexion",
    "nav.profile": "Profil",
    
    // Common actions
    "action.save": "Enregistrer",
    "action.cancel": "Annuler",
    "action.delete": "Supprimer",
    "action.edit": "Modifier",
    "action.create": "Créer",
    "action.search": "Rechercher",
    "action.filter": "Filtrer",
    "action.back": "Retour",
    "action.next": "Suivant",
    "action.submit": "Soumettre",
    "action.upload": "Télécharger",
    "action.download": "Télécharger",
    
    // Landing page
    "landing.hero.title": "Concevez Votre Jardin de Rêve",
    "landing.hero.subtitle": "Planification intelligente pour des jardins durables basés sur les principes de la permaculture",
    "landing.cta.start": "Commencer la Planification",
    "landing.cta.learn": "En Savoir Plus",
    "landing.features.title": "Fonctionnalités",
    "landing.curb_appeal": "Ajoutez de l'attrait à votre propriété",
    
    // Garden planning
    "garden.title": "Planificateur de Jardin",
    "garden.new": "Nouveau Plan de Jardin",
    "garden.saved": "Plans Enregistrés",
    "garden.location": "Emplacement",
    "garden.dimensions": "Dimensions",
    "garden.aesthetic": "Style de Jardin",
    
    // Weather
    "weather.title": "Prévisions Météo",
    "weather.current": "Conditions Actuelles",
    "weather.forecast": "Prévisions sur 7 Jours",
    "weather.historical": "Données Historiques",
    "weather.advisory": "Conseils de Jardinage",
    
    // Plants
    "plants.title": "Base de Données des Plantes",
    "plants.search": "Rechercher des plantes...",
    "plants.spacing": "Espacement",
    "plants.sun": "Besoins en Soleil",
    "plants.water": "Besoins en Eau",
    "plants.harvest": "Jours jusqu'à la Récolte",
    
    // Visual Design Tool
    "design.title": "Outil de Conception Visuelle",
    "design.upload": "Télécharger une Photo",
    "design.plants": "Plantes",
    "design.warnings": "Avertissements",
    "design.spacing_warning": "Problème d'Espacement",
    "design.rotation_warning": "Avertissement de Rotation des Cultures",
    "design.no_warnings": "Aucun avertissement ! Votre aménagement de jardin est parfait.",
    "design.previous_season": "Saison Précédente",
    "design.current_season": "Saison Actuelle",
    
    // Pricing
    "pricing.title": "Choisissez Votre Plan",
    "pricing.free": "Gratuit",
    "pricing.standard": "Le Jardinier",
    "pricing.premium": "Jardinier Expert",
    "pricing.lifetime": "Accès à Vie",
    "pricing.monthly": "par mois",
    "pricing.yearly": "par an",
    "pricing.one_time": "paiement unique",
    
    // Auth
    "auth.login": "Connexion",
    "auth.logout": "Déconnexion",
    "auth.login_required": "Veuillez vous connecter pour continuer",
    
    // Messages
    "msg.success": "Succès !",
    "msg.error": "Une erreur s'est produite",
    "msg.loading": "Chargement...",
    "msg.saving": "Enregistrement...",
  }
};

const LanguageContext = createContext(null);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguageState] = useState(() => {
    // Try to get from localStorage, default to 'en'
    if (typeof window !== 'undefined') {
      return localStorage.getItem('rootspace_language') || 'en';
    }
    return 'en';
  });
  
  const [customTranslations, setCustomTranslations] = useState({});
  const [translating, setTranslating] = useState(false);
  
  const supportedLanguages = {
    en: { name: "English", flag: "🇺🇸" },
    es: { name: "Español", flag: "🇪🇸" },
    fr: { name: "Français", flag: "🇫🇷" }
  };
  
  // Set language and persist to localStorage
  const setLanguage = useCallback((lang) => {
    if (supportedLanguages[lang]) {
      setLanguageState(lang);
      localStorage.setItem('rootspace_language', lang);
    }
  }, []);
  
  // Get translation for a key
  const t = useCallback((key, fallback = null) => {
    // Check custom translations first
    if (customTranslations[language]?.[key]) {
      return customTranslations[language][key];
    }
    
    // Check default translations
    if (DEFAULT_TRANSLATIONS[language]?.[key]) {
      return DEFAULT_TRANSLATIONS[language][key];
    }
    
    // Fallback to English
    if (DEFAULT_TRANSLATIONS.en?.[key]) {
      return DEFAULT_TRANSLATIONS.en[key];
    }
    
    // Return fallback or key
    return fallback || key;
  }, [language, customTranslations]);
  
  // Translate dynamic text via API
  const translateText = useCallback(async (text, targetLang = null) => {
    const target = targetLang || language;
    
    if (target === 'en') {
      return text;
    }
    
    try {
      const response = await axios.post(`${API}/translate`, {
        text,
        target_language: target,
        source_language: 'en'
      });
      return response.data.translated_text;
    } catch (error) {
      console.error("Translation error:", error);
      return text;
    }
  }, [language]);
  
  // Translate multiple texts at once
  const translateBatch = useCallback(async (texts, targetLang = null) => {
    const target = targetLang || language;
    
    if (target === 'en') {
      return texts;
    }
    
    setTranslating(true);
    try {
      const response = await axios.post(`${API}/translate-batch`, {
        texts,
        target_language: target,
        source_language: 'en'
      });
      return response.data.translations;
    } catch (error) {
      console.error("Batch translation error:", error);
      return texts;
    } finally {
      setTranslating(false);
    }
  }, [language]);
  
  // Add custom translations (e.g., from admin panel)
  const addTranslations = useCallback((lang, translations) => {
    setCustomTranslations(prev => ({
      ...prev,
      [lang]: {
        ...(prev[lang] || {}),
        ...translations
      }
    }));
  }, []);
  
  const value = {
    language,
    setLanguage,
    supportedLanguages,
    t,
    translateText,
    translateBatch,
    translating,
    addTranslations
  };
  
  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export default LanguageContext;
